import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import type {
  ActivityFeedEntry,
  ConversationEntry,
  StudioId,
} from '@/domain/types';

// Outbound + inbound call orchestration. Mock simulation of the PRD §15.1/15.2 flow.
// All "real" services (Twilio, Dialpad, Retell) are simulated by timers + brain mutations.

export interface ActiveCallContext {
  id: string;
  customerId: string;
  studioId: StudioId;
  direction: 'inbound' | 'outbound';
  startedAt: string;
  transcript: TranscriptLine[];
  status: 'connecting' | 'ringing' | 'live' | 'wrapping' | 'completed';
}

export interface TranscriptLine {
  id: string;
  speaker: 'customer' | 'agent';
  body: string;
  atIso: string;
}

const listeners = new Set<(c: ActiveCallContext | null) => void>();
let activeCall: ActiveCallContext | null = null;

export function getActiveCall() {
  return activeCall;
}

export function onActiveCallChange(handler: (c: ActiveCallContext | null) => void) {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}

function notifyListeners() {
  for (const l of listeners) {
    try {
      l(activeCall);
    } catch (err) {
      console.error('[callFlow] listener error', err);
    }
  }
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

function appendActivity(a: Omit<ActivityFeedEntry, 'id' | 'timestamp'>) {
  useBrainStore.getState().appendActivity({
    ...a,
    id: `act-call-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    timestamp: nowIso(),
  });
}

const SARAH_SCRIPT: TranscriptLine[] = [
  { id: 't1', speaker: 'agent', body: 'Hi Sarah, it\'s Lydia from Tattoo Vault Coburg.', atIso: '' },
  { id: 't2', speaker: 'customer', body: 'Hey, glad you got back to me. Sorry about the early hours yesterday.', atIso: '' },
  {
    id: 't3',
    speaker: 'agent',
    body: 'No worries at all. I\'ve got your Saturday slot with Pat still here. Half-day, $400 deposit.',
    atIso: '',
  },
  {
    id: 't4',
    speaker: 'customer',
    body: 'Yeah let\'s lock it in. Can you send me the link to pay now?',
    atIso: '',
  },
  {
    id: 't5',
    speaker: 'agent',
    body: 'Sending it through now. See you Saturday at 11.',
    atIso: '',
  },
];

const GENERIC_OUTBOUND_SCRIPT: TranscriptLine[] = [
  { id: 't1', speaker: 'agent', body: 'Hi, it\'s the team from Tattoo Vault.', atIso: '' },
  { id: 't2', speaker: 'customer', body: 'Hey, thanks for calling back.', atIso: '' },
  { id: 't3', speaker: 'agent', body: 'I have your record open here. Want me to talk you through next steps?', atIso: '' },
];

export function startOutboundCall(opts: { customerId: string; studioId: StudioId }) {
  const id = `call-${Date.now()}`;
  activeCall = {
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    direction: 'outbound',
    startedAt: nowIso(),
    transcript: [],
    status: 'connecting',
  };
  notifyListeners();
  playSound('inbound-ring');
  setTimeout(() => connectCall(opts.customerId === 'sarah-holman' ? SARAH_SCRIPT : GENERIC_OUTBOUND_SCRIPT), 1800);
  return id;
}

export function answerInboundCall(callId: string) {
  if (!activeCall || activeCall.id !== callId) return;
  connectCall(SARAH_SCRIPT);
}

function connectCall(script: TranscriptLine[]) {
  if (!activeCall) return;
  activeCall = { ...activeCall, status: 'live' };
  notifyListeners();
  appendActivity({
    type: 'outbound-call',
    customerId: activeCall.customerId,
    studioId: activeCall.studioId,
    body: `Outbound call connected to ${activeCall.customerId}.`,
  });
  // Transcript streams into activeCall.transcript only. The LiveCallBanner
  // renders it live. On hang-up we consolidate into ONE conversation entry.
  // Cadence tuned for the live demo: ~2.4s between utterances feels
  // conversational, slow enough to read along, fast enough not to drag.
  const UTTERANCE_INTERVAL_MS = 2400;
  script.forEach((line, idx) => {
    setTimeout(() => {
      if (!activeCall) return;
      const atIso = nowIso();
      const lineId = `${line.id}-${activeCall.id}`;
      activeCall = {
        ...activeCall,
        transcript: [...activeCall.transcript, { ...line, id: lineId, atIso }],
      };
      notifyListeners();
    }, idx * UTTERANCE_INTERVAL_MS);
  });
}

export function endCall(disposition: {
  tags: string[];
  notes?: string;
}) {
  if (!activeCall) return;
  const finished: ActiveCallContext = { ...activeCall, status: 'completed' };
  const endIso = nowIso();
  const durationSec = Math.max(
    1,
    Math.round((new Date(endIso).getTime() - new Date(finished.startedAt).getTime()) / 1000),
  );
  const brain = useBrainStore.getState();

  // Resolve the operator on the call so per-agent call reporting (Prompt F) has a
  // durable, real attribution instead of a hardcoded name.
  const agentId = useSessionStore.getState().agentId ?? undefined;
  const agent = agentId ? brain.agents[agentId] : undefined;
  const authorName = agent ? `${agent.firstName} ${agent.lastName}` : 'Lydia';

  const dispoBits = [
    disposition.tags.length > 0 ? `Disposition: ${disposition.tags.join(', ')}` : null,
    disposition.notes,
  ]
    .filter(Boolean)
    .join(' · ');

  const entry: ConversationEntry = {
    id: `convo-call-${finished.id}`,
    customerId: finished.customerId,
    channel: 'voice',
    direction: finished.direction === 'inbound' ? 'in' : 'out',
    body: `${finished.direction === 'inbound' ? 'Inbound' : 'Outbound'} call · ${durationSec}s · ${finished.transcript.length} utterances${dispoBits ? ` · ${dispoBits}` : ''}`,
    timestamp: endIso,
    authorRole: 'agent',
    authorName,
    ...(agentId ? { agentId } : {}),
    transcript: finished.transcript.map((t) => ({
      speaker: t.speaker,
      body: t.body,
      atIso: t.atIso,
    })),
  };
  brain.addConversationEntry(finished.customerId, entry);

  appendActivity({
    type: 'call-completed',
    customerId: finished.customerId,
    studioId: finished.studioId,
    body: `Call complete. ${dispoBits || 'No disposition'}.`,
  });
  eventBus.publish('call:completed', { customerId: finished.customerId });
  activeCall = null;
  notifyListeners();
}

export function dispatchInboundCall(opts: { customerId: string; studioId: StudioId }) {
  const id = `inbound-${Date.now()}`;
  activeCall = {
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    direction: 'inbound',
    startedAt: nowIso(),
    transcript: [],
    status: 'ringing',
  };
  useUIStore.getState().addIncomingCall({
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    startedAt: nowIso(),
  });
  playSound('inbound-ring');
  notifyListeners();
}

export function declineInboundCall(callId: string) {
  if (!activeCall || activeCall.id !== callId) return;
  activeCall = null;
  notifyListeners();
}
